import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Building2 } from 'lucide-react';
import AuthFormContainer from '@/features/auth/signup/components/AuthFormContainer.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import NoItems from '@/components/ui/NoItems.tsx';
import LoadError from '@/components/ui/LoadError.tsx';
import { SectionLabel } from '@/components/ui/Title.tsx';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { getMeQueryOptions } from '@/features/dashboard/_root/hooks/useMe.ts';
import { readStoredCompanyId } from '@/features/dashboard/_root/access/currentCompany.ts';
import useSignOut from '@/features/auth/login/hooks/useSignOut.ts';
import WorkspaceCompanyCard from '@/features/auth/workspaces/components/WorkspaceCompanyCard.tsx';
import PendingInvitationCard from '@/features/auth/workspaces/components/PendingInvitationCard.tsx';
import WorkspacesSkeleton from '@/features/auth/workspaces/components/WorkspacesSkeleton.tsx';

const WorkspacesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { signOut } = useSignOut();
  const { data: me, isPending, isError } = useQuery(getMeQueryOptions(user));

  if (isPending) return <WorkspacesSkeleton />;
  if (isError || !me) {
    return (
      <AuthFormContainer title="Twoje firmy">
        <LoadError subject="listy firm" />
      </AuthFormContainer>
    );
  }

  // Tylko wybór zapamiętany — bez niego żadna firma nie jest „bieżąca”, choć aplikacja weszłaby do pierwszej.
  const storedCompanyId = readStoredCompanyId();

  return (
    <AuthFormContainer title="Twoje firmy">
      <div className="flex w-full flex-col gap-6">
        <section className="flex w-full flex-col gap-3">
          <SectionLabel>Firmy</SectionLabel>
          {me.accesses.length === 0 ? (
            <NoItems
              icon={<Building2 />}
              title="Nie masz jeszcze dostępu do żadnej firmy"
              description="Przyjmij zaproszenie poniżej albo załóż własną firmę."
              compact
            />
          ) : (
            me.accesses.map((access) => (
              <WorkspaceCompanyCard
                key={access.companyId}
                access={access}
                current={access.companyId === storedCompanyId}
              />
            ))
          )}
        </section>

        {me.invitations.length > 0 && (
          <section className="flex w-full flex-col gap-3">
            <SectionLabel>Zaproszenia</SectionLabel>
            {me.invitations.map((invitation) => (
              <PendingInvitationCard
                key={invitation.invitationId}
                invitation={invitation}
              />
            ))}
          </section>
        )}

        <div className="flex w-full flex-col items-center gap-3 border-t border-neutral-200 pt-6">
          <ActionButton
            tone="outline"
            size="block"
            onClick={() =>
              navigate({
                to: '/auth/complete-profile',
                search: { newCompany: true },
              })
            }
          >
            Załóż nową firmę
          </ActionButton>
          <p className="text-sm text-neutral-500">
            Zalogowano jako{' '}
            <span className="font-medium text-base-text">{user?.email}</span>
          </p>
          <ActionButton tone="ghost" size="sm" onClick={signOut}>
            Wyloguj się
          </ActionButton>
        </div>
      </div>
    </AuthFormContainer>
  );
};

export default WorkspacesPage;
